import { IconButton } from '@mobile/components/kartoy/icon-button'
import { PhoneBanner } from '@mobile/components/kartoy/phone-banner'
import { PillButton } from '@mobile/components/kartoy/pill-button'
import { ScreenCardDetails } from '@mobile/screens/card-details'
import { HomeOperations } from '@mobile/screens/home/operations'
import {
  type BackendCard,
  type CardTemplateId,
  fetchBanks,
  fetchCards,
  fetchCurrentUser,
  isCardIssuePaymentActive,
  isIssuedCard,
  emailPromptShown as markEmailPromptShown,
  matchesCardTemplate,
  selectedCardIssue,
  selectedCards,
  selectedCurrentUser,
  selectedEmailPromptShown,
} from '@shared-core/features/backend'
import { useNotifications } from '@shared-core/features/notifications'
import { useAuthorizationGate } from '@shared-core/hooks/use-authorization-gate'
import { useCardOperationAvailability } from '@shared-core/hooks/use-card-operation-availability'
import { useCardTemplates } from '@shared-core/hooks/use-card-templates'
import { useEvent } from '@shared-core/hooks/use-event'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { useWithdrawAvailability } from '@shared-core/hooks/use-withdraw-availability'
import { PhoneEntryMode, ScreensList, useFocusEffect, useNavigation } from '@shared-core/navigation'
import { useAppDispatch, useAppSelector } from '@shared-core/store'
import { formatWithThousandSeparators } from '@shared-core/utils/balances/balances'
import { Big } from '@shared-core/utils/balances/big'
import { AppAnimatedBalance } from '@shared-ui/components/app/animated-balance'
import { AppBankCard } from '@shared-ui/components/app/bank-card'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { Column, Flex, Row } from '@shared-ui/components/ui/grid'
import { ScreenWrapper } from '@shared-ui/components/ui/screen-wrappers/screen-wrapper'
import { GText } from '@shared-ui/components/ui/text'
import { useBalanceHider } from '@shared-ui/hooks/ui/use-balance-hider'
import { isTelegramMiniApp } from '@shared-ui/platform/telegram'
import { useAppTheme } from '@shared-ui/theme'
import { rh, rw } from '@shared-ui/theme/scale'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Easing, Animated as NativeAnimated, Pressable, ScrollView, View } from 'react-native'
import Animated, { FadeInDown } from 'react-native-reanimated'

import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

const AnimatedPressable = Animated.createAnimatedComponent(Pressable)

const HIDDEN_BALANCE = '••••'

function getCardsBalance(cards: BackendCard[] | null): string {
  return (cards ?? []).filter(isIssuedCard).reduce((total, card) => Big(total).plus(card.balance).toString(), '0')
}

// В ожидании оплаты карта ещё не доступна для операций и реквизитов.
function getCardId(cards: BackendCard[] | null, cardType: CardTemplateId): string | null {
  return cards?.find((card) => isIssuedCard(card) && matchesCardTemplate(card, cardType))?.id ?? null
}

// Карточка продукта (Виртуальная / Platinum) с press-анимацией.
const ProductCard = memo(function ProductCardComponent({
  type,
  label,
  price,
  isIssued,
  isSingle,
  balance,
  isBalanceShow,
  onPress,
}: {
  type: CardTemplateId
  label: string
  price?: string
  isIssued: boolean
  isSingle: boolean
  balance: string
  isBalanceShow: boolean
  onPress: (type: CardTemplateId) => void
}) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const handlePress = useEvent(() => onPress(type))
  const isHorizontal = isSingle && !isIssued
  let displayedValue = price ? `₽${formatWithThousandSeparators(price.replace(/\.00$/, ''))}` : '—'

  if (isIssued) displayedValue = isBalanceShow ? `$${formatWithThousandSeparators(balance)}` : HIDDEN_BALANCE

  return (
    <AnimatedPressable onPress={handlePress} style={[styles.productCard, isHorizontal && styles.productCardHorizontal]}>
      <Row justify="space-between" align="center">
        <AppBankCard type={type} isNumberVisible={false} />
      </Row>
      {isIssued && (
        <Column gap={2} style={[styles.productMeta, styles.productMetaIssued]}>
          <GText fw={600} style={styles.productPrice}>
            {displayedValue}
          </GText>
          <GText fw={400} style={styles.productLabel}>
            {label}
          </GText>
        </Column>
      )}
      {!isIssued && (
        <Column gap={12} style={isHorizontal && styles.productDetailsHorizontal}>
          <GText variant="body-16-semibold">{label}</GText>
          <Column gap={2}>
            <GText variant="caption-14-regular" isSecondary>
              {t('cost')}
            </GText>
            <GText fw={600} style={styles.productPrice}>
              {displayedValue}
            </GText>
          </Column>
        </Column>
      )}
    </AnimatedPressable>
  )
})

export function ScreenHome() {
  const styles = useCreateStyles()
  const theme = useAppTheme()
  const { t } = useI18N(locales)
  const { addToast } = useNotifications()
  const navigation = useNavigation()
  const dispatch = useAppDispatch()
  const emailPromptShown = useAppSelector(selectedEmailPromptShown)
  const { isAuthorized, hasPhone: hasUserPhone, ensureAuthorized } = useAuthorizationGate({ requireEmail: true })
  const ensureWithdrawAvailable = useWithdrawAvailability()
  const ensureCardOperationAvailable = useCardOperationAvailability()
  const templates = useCardTemplates()
  const cards = useAppSelector(selectedCards)
  const cardIssue = useAppSelector(selectedCardIssue)
  const currentUser = useAppSelector(selectedCurrentUser)
  const balance = getCardsBalance(cards)
  const { isBalanceShow, toggleGlobal } = useBalanceHider()
  const [productsWidth, setProductsWidth] = useState(0)
  const cardsArrival = useRef(new NativeAnimated.Value(0)).current

  useFocusEffect(
    useCallback(() => {
      cardsArrival.setValue(0)
      const animation = NativeAnimated.timing(cardsArrival, {
        toValue: 1,
        duration: 260,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      })
      animation.start()
      return () => animation.stop()
    }, [cardsArrival]),
  )
  // Шаблоны продуктов плюс выпущенные карты, которых нет в шаблонах; пересчитываем только при изменении данных.
  const productCards = useMemo(() => {
    const items: { id: CardTemplateId; title: string; issuePrice?: string }[] = [...templates]

    for (const card of cards ?? []) {
      if (!isIssuedCard(card) || items.some((product) => matchesCardTemplate(card, product.id))) continue

      let title = card.templateId

      if (card.templateId === 'virtual') title = t('card-virtual')

      if (card.templateId === 'platinum') title = t('card-platinum')

      items.push({ id: card.templateId, title: card.name ?? title })
    }

    return items
  }, [templates, cards, t])
  const scrollProducts = productCards.length > 2

  useEffect(() => {
    if (!isAuthorized) return
    void dispatch(fetchBanks())
    void dispatch(fetchCurrentUser())
  }, [dispatch, isAuthorized])

  // Предложение показываем на главной один раз за вход после загрузки профиля.
  useFocusEffect(
    useCallback(() => {
      if (!isAuthorized || !currentUser || isTelegramMiniApp() || currentUser.email || emailPromptShown) return
      const timer = setTimeout(() => {
        dispatch(markEmailPromptShown())
        navigation.navigate(ScreensList.FORM_SHEET_EMAIL_PROMPT)
      }, 350)
      return () => clearTimeout(timer)
    }, [currentUser, isAuthorized, emailPromptShown, dispatch, navigation]),
  )

  useFocusEffect(
    useCallback(() => {
      if (isAuthorized) void dispatch(fetchCards())
      return undefined
    }, [dispatch, isAuthorized]),
  )

  const balanceValue = isBalanceShow
    ? `$${formatWithThousandSeparators(Big(balance).toFixed(2)).replace('.', ',')}`
    : HIDDEN_BALANCE

  const issuedType = getCardId(cards, 'platinum') ? 'platinum' : 'virtual'
  const issuedId = getCardId(cards, issuedType)

  const hasBothCards = Boolean(getCardId(cards, 'virtual') && getCardId(cards, 'platinum'))

  const handleProductPress = useEvent((type: CardTemplateId) => {
    if (!isAuthorized) {
      navigation.navigate(ScreensList.CARD_INFO, { templateId: type })
      return
    }

    const productCardId = getCardId(cards, type)

    if (productCardId) navigation.navigate(ScreensList.CARD_DETAILS, { cardId: productCardId, templateId: type })
    else if (cardIssue.invoice?.templateId === type && isCardIssuePaymentActive(cardIssue.invoice))
      navigation.navigate(ScreensList.PAYMENT_LINK, { txId: cardIssue.invoice.txId })
    else navigation.navigate(ScreensList.CARD_INFO, { templateId: type })
  })

  if (issuedId) return <ScreenCardDetails isHome initialCardId={issuedId} initialTemplateId={issuedType} />

  const requireIssuedCard = () => {
    if (hasUserPhone && currentUser?.email && !cards?.some(isIssuedCard)) {
      const issuingCard = cards?.find((card) => card.status === 'pending' || card.status === 'processing')

      if (issuingCard) return ensureCardOperationAvailable('topup', issuingCard.id)

      addToast({ text: t('issue-card-first'), isClosable: false, timeout: 3500, override: true })
      return false
    }
    return true
  }

  const handleTopup = () => {
    if (!ensureAuthorized(true) || !requireIssuedCard()) return

    if (issuedId && !ensureCardOperationAvailable('topup', issuedId)) return

    if (hasBothCards) navigation.navigate(ScreensList.FORM_SHEET_SELECT_CARD, { action: 'topup' })
    else navigation.navigate(issuedId ? ScreensList.TOPUP_AMOUNT : ScreensList.FORM_SHEET_ISSUE_CARD)
  }
  const handleWithdraw = async () => {
    if (!ensureAuthorized(true) || !requireIssuedCard()) return

    if (!ensureCardOperationAvailable('withdraw', issuedId ?? undefined) || !(await ensureWithdrawAvailable())) return

    if (hasBothCards) navigation.navigate(ScreensList.FORM_SHEET_SELECT_CARD, { action: 'withdraw' })
    else navigation.navigate(issuedId ? ScreensList.FORM_SHEET_WITHDRAW_BANK : ScreensList.FORM_SHEET_ISSUE_CARD)
  }

  // Карточки продукта: цена — строкой, отображение через format-хелпер.
  const productCardsContent = (
    <NativeAnimated.View
      style={[
        styles.products,
        {
          opacity: cardsArrival.interpolate({ inputRange: [0, 1], outputRange: [0.6, 1] }),
          transform: [{ translateY: cardsArrival.interpolate({ inputRange: [0, 1], outputRange: [rh(10), 0] }) }],
        },
      ]}
      onLayout={(event) => setProductsWidth(event.nativeEvent.layout.width)}
    >
      <ScrollView
        horizontal
        scrollEnabled={scrollProducts}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.productsContent}
      >
        {productCards.map((card) => {
          const cardId = getCardId(cards, card.id)
          const cardBalance = cards?.find((item) => item.id === cardId)?.balance ?? '0'
          return (
            <View
              key={card.id}
              style={
                scrollProducts
                  ? [styles.productTile, { width: Math.max(0, (productsWidth - rw(16)) / 2) }]
                  : styles.productTileFlexible
              }
            >
              <ProductCard
                type={card.id}
                label={card.title}
                price={card.issuePrice}
                isIssued={Boolean(cardId)}
                isSingle={productCards.length === 1}
                isBalanceShow={isBalanceShow}
                balance={cardBalance}
                onPress={handleProductPress}
              />
            </View>
          )
        })}
      </ScrollView>
    </NativeAnimated.View>
  )

  return (
    <ScreenWrapper showsVerticalScrollIndicator={false} style={styles.container}>
      <Row align="center" justify="space-between">
        <Pressable onPress={() => navigation.navigate(ScreensList.FORM_SHEET_PROFILE)} style={styles.avatar}>
          <AppIcon icon="profile" size={rw(40)} color={theme.COLOR_TEXT_TERTIARY} hasCircle={false} />
        </Pressable>
        <IconButton icon="help" onPress={() => navigation.navigate(ScreensList.FORM_SHEET_FAQ)} size={40} />
      </Row>
      <View style={styles.balanceWrap}>
        <Pressable accessibilityRole="button" onPress={toggleGlobal}>
          <AppAnimatedBalance balance={balanceValue} balanceKey={`${isBalanceShow}:${balanceValue}`} />
        </Pressable>
      </View>
      <View style={styles.actionsWrap}>
        <Row gap={16}>
          <Flex>
            <PillButton title={t('topup')} icon="topup" onPress={handleTopup} />
          </Flex>
          <Flex>
            <PillButton title={t('withdraw')} icon="withdraw" variant="secondary" onPress={handleWithdraw} />
          </Flex>
        </Row>
      </View>
      {!hasUserPhone && (
        <Animated.View entering={FadeInDown.delay(140).duration(420)} style={styles.bannerWrap}>
          <PhoneBanner onPress={() => navigation.navigate(ScreensList.ENTRY_PHONE, { mode: PhoneEntryMode.BIND })} />
        </Animated.View>
      )}
      {productCardsContent}
      <HomeOperations />
    </ScreenWrapper>
  )
}
